import React, {useEffect} from 'react';
import LomavisCard1 from '../../../generic/cards/LomavisCard1/LomavisCard1';
import LomavisButtonToggle1 from '../../../generic/buttons/LomavisButtonToggle1';
import {useTranslate} from '@tolgee/react';
import {useDispatch, useSelector} from 'react-redux';
import {
    deleteGroup,
    setApprovalConfiguration,
    setApprovalKey,
    setApprovalUI
} from '../../../../redux/slices/LomavisPostApproval/lomavisPostApproval';
import GroupSelectTable from '../groupSelectTable';
import UserSelectTable from '../userSelectTable';
import {
    STEP_1,
    STEP_2,
    STEP_MODE_MULTI_STEP, TableSelectOption,
} from '../../../../redux/slices/LomavisPostApproval/constants';
import {approvalStepRuleOptions} from '../constants';
import {CreateGroupModal} from '../groupModal/createGroupModal';
import {EditGroupModal} from '../groupModal/editGroupModal';
import DeleteConfirmationModal from '../groupModal/deleteGroupConfimationModal';
import {lomavisShowToastr} from '../../../../redux/slices/utils/ui/toastr';
import WhoHasToApprove from '../whoHasToApprove';

const RequestApprovalCard: React.FC = () => {
    const {t} = useTranslate();
    const dispatch = useDispatch();
    const {
        featuresEnabled,
        ui: {
            showCreateGroupModal,
            showEditGroupModal,
            showDeleteConfirmationModal
        },
        data: {
            select_type,
            step_mode,
            current_step,
            approval_step_1_users_rule,
            approval_step_2_users_rule,

            approval_step_1_users,
            approval_step_2_users,
            approval_step_2_group,
            approval_step_1_group
        }
    } = useSelector((state: any) => state.lomavisPostApproval);

    const onSelectTypeChange = (value: string): void => {
        let showError;
        if (current_step === STEP_1) {
            showError = approval_step_1_users?.length > 0 || approval_step_1_group;
        } else {
            showError = approval_step_2_users?.length > 0 || approval_step_2_group;
        }

        if (showError) {
            lomavisShowToastr(
                t('lomavisPostApproval.configType.cantSwitch'),
                'error'
            );
            return;
        }

        dispatch(setApprovalConfiguration({key: 'select_type', value}));
    };

    const checkFilledApproval = (step) => {
        let selectType = TableSelectOption.USER;

        if (step === STEP_1) {
            if (approval_step_1_group) {
                selectType = TableSelectOption.GROUP;
            } else if (approval_step_1_users?.length > 0) {
                selectType = TableSelectOption.USER;
            }
        } else {
            if (approval_step_2_group) {
                selectType = TableSelectOption.GROUP;
            } else if (approval_step_2_users?.length > 0) {
                selectType = TableSelectOption.USER;
            }
        }

        dispatch(
            setApprovalConfiguration({
                key: 'select_type',
                value: selectType
            })
        );
    };

    return (
        <div>
            <CreateGroupModal
                show={showCreateGroupModal}
                onHide={() => {
                    dispatch(
                        setApprovalUI({key: 'showCreateGroupModal', value: false})
                    );
                }}
            />
            <EditGroupModal
                show={showEditGroupModal}
                onHide={() => {
                    dispatch(setApprovalUI({key: 'showEditGroupModal', value: false}));
                }}
            />
            <DeleteConfirmationModal
                show={showDeleteConfirmationModal}
                handleClose={() => {
                    dispatch(
                        setApprovalUI({key: 'showDeleteConfirmationModal', value: false})
                    );
                }}
                handleDelete={() => {
                    dispatch(deleteGroup() as any);
                    lomavisShowToastr(
                        t("lomavisApproval.delete_group_success"),
                        "success",
                        true
                    );
                }}
            />

            {step_mode === STEP_MODE_MULTI_STEP && (
                <>
                    {' '}
                    <LomavisCard1
                        title={t('lomavisPostApproval.approvalFlow.rightColumn.approvalSteps.title')}
                        helpText={t('lomavisPostApproval.approvalFlow.rightColumn.approvalSteps.helpText')}
                    >
                        <LomavisButtonToggle1
                            buttonLeftFaIconClass="fa-duotone fa-solid fa-check"
                            buttonLeftText={t('lomavisPostApproval.requestApproval.step1')}
                            buttonLeftValue={STEP_1}
                            buttonRightFaIconClass="fa-duotone fa-solid fa-check-double"
                            buttonRightText={t('lomavisPostApproval.requestApproval.step2')}
                            buttonRightValue={STEP_2}
                            value={current_step}
                            onChange={(step) => {
                                dispatch(
                                    setApprovalConfiguration({key: 'current_step', value: step})
                                );

                                checkFilledApproval(step);
                            }}
                        />
                    </LomavisCard1>
                    <div className="mt-3"/>
                </>
            )}
            <LomavisCard1
                title={t('lomavisPostApproval.requestApproval.title')}
                helpText={t('lomavisPostApproval.requestApproval.helpText')}
            >
                <div>
                    <LomavisButtonToggle1
                        buttonLeftFaIconClass="fa-duotone fa-solid fa-user"
                        buttonLeftText={t('lomavisPostApproval.requestApproval.selectUser')}
                        buttonLeftValue="user"
                        buttonLeftShowUpgrade={false}
                        buttonRightFaIconClass="fa-duotone fa-solid fa-user-group"
                        buttonRightText={t(
                            'lomavisPostApproval.requestApproval.selectGroup'
                        )}
                        buttonRightValue="group"
                        buttonRightShowUpgrade={!featuresEnabled.approvalProcessGroups}
                        value={select_type}
                        onChange={onSelectTypeChange}
                    />

                    <div className="mt-3">
                        <div className="justify-content-end align-items-center"
                             style={{
                                 display: select_type === TableSelectOption.USER ? 'none' : 'flex'
                             }}
                        >
                            <div>
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => {
                                        dispatch(
                                            setApprovalUI({
                                                key: 'showCreateGroupModal',
                                                value: true
                                            })
                                        );
                                    }}
                                >
                                    <i className="fa-duotone fa-solid fa-plus" style={{marginRight: '5px'}}/>
                                    {t('lomavisPostApproval.requestApproval.createGroup')}
                                </button>
                            </div>
                        </div>
                        {' '}
                        <div className="mt-2"/>
                        {select_type === TableSelectOption.USER ? (
                            <div className={""}>
                                <WhoHasToApprove
                                    ruleOption={
                                        approvalStepRuleOptions.find(
                                            ({value}) =>
                                                value ===
                                                (current_step === STEP_1
                                                    ? approval_step_1_users_rule
                                                    : approval_step_2_users_rule)
                                        )?.label
                                    }
                                    handleClick={(value) =>
                                        dispatch(
                                            setApprovalKey({
                                                key: `approval_step_${current_step}_users_rule`,
                                                value
                                            })
                                        )
                                    }
                                />
                                <div className="mt-3"/>
                                <UserSelectTable currentStep={current_step}/>
                            </div>
                        ) : (
                            <div>
                                <GroupSelectTable currentStep={current_step}/>
                            </div>
                        )}
                    </div>
                </div>
            </LomavisCard1>
        </div>
    );
};

export default RequestApprovalCard;
